'use client';

import FormLabel from '@c/forms/controls/FormLabel';
import { useQuery } from '@tanstack/react-query';
import Select from '@ui/Select';
import { getSubcatsClient } from '@util/firestore/categories';
import { CategoryDocument } from 'models/category';
import Image from 'next/image';
import { useCreateListing } from '../_context';
import { useEffect } from 'react';

export default function SelectListingCategory() {
  const { form, setCurrentStep, categoryInfo, setCategoryInfo } =
    useCreateListing();

  const { data: categories } = useQuery({
    queryKey: ['categories', categoryInfo[0]?.id],
    queryFn: () => getSubcatsClient(1, categoryInfo[0]?.id),
    enabled: !!categoryInfo[0]?.id,
  });
  const { data: cat2s } = useQuery({
    queryKey: ['category2s', categoryInfo[1]?.id],
    queryFn: () => getSubcatsClient(2, categoryInfo[1]?.id),
    enabled: !!categoryInfo[1]?.id,
  });
  const { data: cat3s } = useQuery({
    queryKey: ['category3s', categoryInfo[2]?.id],
    queryFn: () => getSubcatsClient(3, categoryInfo[2]?.id),
    enabled: !!categoryInfo[2]?.id,
  });
  const { data: cat4s } = useQuery({
    queryKey: ['category4s', categoryInfo[3]?.id],
    queryFn: () => getSubcatsClient(4, categoryInfo[3]?.id),
    enabled: !!categoryInfo[3]?.id,
  });

  const canContinue =
    categoryInfo[1]?.name === 'Bikes' ||
    categoryInfo[0]?.name === 'Casual' ||
    categoryInfo.length > 2;

  const renderSubcategorySelect = (
    options: CategoryDocument[],
    index: number
  ) => {
    return (
      <>
        <Select
          options={
            options
              .filter((o) => !o.is_seo_only)
              .map((option) => ({
                id: option.id,
                label: option.name,
                value: option.id,
              })) || []
          }
          key={options[0]?.id}
          allowEmpty={false}
          className="bg-white"
          placeholder="Select a subcategory"
          onChange={(option) => {
            const currCategoryInfo = [...categoryInfo];
            currCategoryInfo[index] = options.find(
              (o) => o.id === option.value
            )!;
            setCategoryInfo(currCategoryInfo);
          }}
          value={categoryInfo[index]?.id ?? ''}
        />
      </>
    );
  };

  const handleContinue = () => {
    if (!canContinue) {
      form.setError('category', {
        type: 'required',
        message: 'Please select a category',
      });
      return;
    }
    const lastCat = categoryInfo[categoryInfo.length - 1];
    if (!lastCat) return;
    form.setValue('ride_types', lastCat.ride_types!);
    form.setValue('category', lastCat.product_cats.category!);
    if (lastCat.product_cats.category !== 'Dirt Bikes') {
      form.setValue('category1', lastCat.product_cats.category1!);
      form.setValue('category2', lastCat.product_cats.category2!);
      form.setValue('category3', lastCat.product_cats.category3!);
    }

    form.setValue(
      'categories',
      [
        lastCat.product_cats.category!,
        lastCat.product_cats.category1!,
        lastCat.product_cats.category2!,
        lastCat.product_cats.category3!,
      ].filter(Boolean)
    );
    setCurrentStep(3);
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  return (
    <>
      <div className="relative h-[100rem] w-full py-16 sm:mt-0 sm:px-0 sm:py-0 ">
        <div className="absolute top-1/2 hidden sm:block">
          <Image
            src={categoryInfo[0]?.image ?? ''}
            alt={categoryInfo[0]?.name ?? ''}
            height={300}
            width={300}
            className={`aspect-square h-[30rem] w-auto object-cover object-right`}
          />
        </div>

        <div className="z-10 flex h-full flex-col gap-y-8  px-8 sm:mx-auto sm:my-32 sm:max-w-[60rem]">
          <h1 className="text-[3rem] font-semibold text-black">Categories</h1>

          <div className="mt-16">
            <FormLabel
              value="Categories"
              className="!text-[1.8rem]"
              required={true}
            >
              <Select
                options={
                  categories
                    ?.filter((o) => !o.is_seo_only)
                    .map((section) => {
                      return {
                        id: section.id,
                        label: section.name,
                        value: section.id,
                      };
                    }) ?? []
                }
                className="bg-white"
                value={categoryInfo[1]?.id ?? ''}
                placeholder="Select a category"
                onChange={(option) => {
                  const currCategoryInfo = [...categoryInfo];
                  currCategoryInfo[1] = categories?.find(
                    (o) => o.id === option.value
                  )!;
                  setCategoryInfo(currCategoryInfo);
                }}
                allowEmpty={false}
              />
            </FormLabel>
          </div>
          {!!cat2s?.length && (
            <>
              <div className="my-8 h-px w-full bg-brand-light-gray" />
              <FormLabel value="Subcategory" className="!text-[1.8rem]">
                <div className="flex flex-col gap-y-6">
                  {!!cat2s.length && renderSubcategorySelect(cat2s, 2)}

                  {!!cat3s?.length && renderSubcategorySelect(cat3s, 3)}

                  {!!cat4s?.length && renderSubcategorySelect(cat4s, 4)}
                </div>
              </FormLabel>
            </>
          )}
          <div className="mt-16 flex w-full flex-col items-center gap-[1rem]">
            {form.formState.errors?.category?.message && (
              <p className="text-red-500">
                {form.formState.errors?.category?.message}
              </p>
            )}
            <button
              className="flex w-[25rem] grow items-center justify-center gap-x-2 rounded-[1.5rem] bg-brand-lightest-gray px-8 py-4 text-[1.8rem] text-black hover:bg-brand-light-gray sm:grow-0"
              type="button"
              onClick={() => {
                setCurrentStep(1);
                form.setValue('category1', null);
                form.setValue('category2', null);
                form.setValue('category3', null);
                form.clearErrors('category');
                form.clearErrors('category1');
                form.clearErrors('category2');
                form.clearErrors('category3');
              }}
            >
              Back
            </button>

            <button
              className="flex w-[25rem] grow items-center justify-center gap-x-2 rounded-[1.5rem] bg-brand-secondary px-8 py-4 text-[1.8rem] text-white hover:bg-brand-secondary-darker disabled:cursor-default disabled:bg-brand-lightest-gray disabled:text-black sm:grow-0"
              type="button"
              onClick={handleContinue}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
